import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  RouterProvider,
} from '@tanstack/react-router'
import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { Shell } from '@/components/shell'
import { ToastProvider } from '@/components/toast'
import { NAV } from '@/lib/nav'
import { Avis } from '@/routes/avis'
import { ClientDetailPage } from '@/routes/client-detail'
import { Clients } from '@/routes/clients'
import { CodesPromo } from '@/routes/codes-promo'
import { CommandeDetailPage } from '@/routes/commande-detail'
import { Commandes } from '@/routes/commandes'
import { ConnexionPage } from '@/routes/connexion'
import { MotDePasseOubliePage } from '@/routes/mot-de-passe-oublie'
import { MotDePasseReinitPage } from '@/routes/mot-de-passe-reinit'
import { Dashboard } from '@/routes/dashboard'
import { Journal } from '@/routes/journal'
import { Livraisons } from '@/routes/livraisons'
import { Modeles } from '@/routes/modeles'
import { PagesLegales } from '@/routes/pages-legales'
import { Paiements } from '@/routes/paiements'
import { PaniersAbandonnes } from '@/routes/paniers-abandonnes'
import { ProduitDetailPage } from '@/routes/produit-detail'
import { ProduitNouveauPage } from '@/routes/produit-nouveau'
import { Produits } from '@/routes/produits'
import { Rapports } from '@/routes/rapports'
import { Receptions } from '@/routes/receptions'
import { Reglages } from '@/routes/reglages'
import { Retours } from '@/routes/retours'
import { Stocks } from '@/routes/stocks'
import { Utilisateurs } from '@/routes/utilisateurs'
import './styles.css'

const rootRoute = createRootRoute({
  component: () => <Outlet />,
})

const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'app',
  component: () => (
    <Shell>
      <Outlet />
    </Shell>
  ),
})

const connexionRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/connexion',
  component: ConnexionPage,
})

const oublieRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/connexion/oublie',
  component: MotDePasseOubliePage,
})

const reinitRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/connexion/reinitialiser',
  validateSearch: (s: Record<string, unknown>) => ({
    token: typeof s.token === 'string' ? s.token : undefined,
  }),
  component: MotDePasseReinitPage,
})

const PAGES: Record<string, () => React.JSX.Element> = {
  '/': () => <Dashboard />,
  '/stocks': () => <Stocks />,
  '/produits': () => <Produits />,
  '/commandes': () => <Commandes />,
  '/retours': () => <Retours />,
  '/paniers-abandonnes': () => <PaniersAbandonnes />,
  '/codes-promo': () => <CodesPromo />,
  '/paiements': () => <Paiements />,
  '/receptions': () => <Receptions />,
  '/avis': () => <Avis />,
  '/clients': () => <Clients />,
  '/livraisons': () => <Livraisons />,
  '/utilisateurs': () => <Utilisateurs />,
  '/modeles': () => <Modeles />,
  '/pages-legales': () => <PagesLegales />,
  '/reglages': () => <Reglages />,
  '/rapports': () => <Rapports />,
  '/journal': () => <Journal />,
}

const indexRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/',
  component: Dashboard,
})

const navRoutes = NAV.flatMap((g) => g.entrees)
  .filter((e) => e.to !== '/')
  .map((e) =>
    createRoute({
      getParentRoute: () => appRoute,
      path: e.to,
      component: PAGES[e.to] ?? (() => <Dashboard />),
    }),
  )

const produitNouveauRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/produits/nouveau',
  component: ProduitNouveauPage,
})

const produitDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/produits/$id',
  component: ProduitDetailPage,
})

const commandeDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/commandes/$ref',
  component: CommandeDetailPage,
})

const clientDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/clients/$id',
  component: ClientDetailPage,
})

const router = createRouter({
  routeTree: rootRoute.addChildren([
    connexionRoute,
    oublieRoute,
    reinitRoute,
    appRoute.addChildren([
      indexRoute,
      produitNouveauRoute,
      produitDetailRoute,
      commandeDetailRoute,
      clientDetailRoute,
      ...navRoutes,
    ]),
  ]),
  defaultPreload: 'intent',
})

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}

const queryClient = new QueryClient({
  defaultOptions: { queries: { staleTime: 30_000, retry: 1 } },
})

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        <RouterProvider router={router} />
      </ToastProvider>
    </QueryClientProvider>
  </StrictMode>,
)
